<template>
    <div id="imageMessage" class="image-message-container" :class="{ received }">
        <div
            class="sc-message--text message-bubble"
            :style="{
                ...messageColors,
                'background-color': messageColors.backgroundColor,
            }"
        >
            <span v-if="isGroupMessage" class="whatsapp-number-first" @click.stop="showWhatsappChatStarterModal = true">
                <a href="javascript:void(0)" :style="{ color: getWhatsappNumberColor() }"
                    >{{ whatsappAuthorName }} - {{ whatsappAuthorNumber }}</a
                >
            </span>
            <viewer :id="'imageViewer-' + getId()" :images="[imageSrc()]" v-if="imageSrc()">
                <div
                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                    :content="$t('chatbotv2.imageView')"
                    class="cb-img-preview"
                    @click="show"
                    @mouseenter="hovered = true"
                    @mouseleave="hovered = false"
                >
                    <img
                        :id="'img-' + getId()"
                        :src="imageSrc()"
                        class="img-thumbnail ant-upload-list-picture-card-container"
                        style="object-fit: cover"
                    />
                    <md-icon v-if="hovered" class="cb-pr-icon"> visibility</md-icon>
                </div>
            </viewer>
            <div v-if="getCaption()" class="caption-text">
                <span class="sc-message--text-content">{{ getCaption() }}</span>
            </div>
            <div
                v-if="isGroupMessage"
                style="display: flex; flex-direction: row; justify-content: end; align-items: center; padding: 0 0.3rem"
            >
                <span class="sc-message-meta-new whatsapp-number" @click.stop="showWhatsappChatStarterModal = true">
                    <a href="javascript:void(0)" :style="{ color: getWhatsappNumberColor() }"
                        >{{ whatsappAuthorName }} - {{ whatsappAuthorNumber }}</a
                    >
                </span>
            </div>
        </div>
        <Modal2
            v-if="showWhatsappChatStarterModal"
            @cancel="showWhatsappChatStarterModal = false"
            :title="$t('message.whatsappChatStart')"
            :showFooter="false"
        >
            <WhatsappChatStarter
                :default-phone-number="whatsappAuthorNumber"
                channel-override
                phone-number-override
                @close="showWhatsappChatStarterModal = false"
                :default-name="whatsappAuthorName"
            />
        </Modal2>
    </div>
</template>

<script>
import "viewerjs/dist/viewer.css";
import Viewer from "v-viewer";
import Vue from "vue";
import _ from "lodash";
import Modal2 from "../../common/Modal2.vue";
import WhatsappChatStarter from "../../../views/Pages/Integrations/meta-leads/WhatsappChatStarter.vue";
import StringUtils from "../../../utils/StringUtils";

Vue.use(Viewer);

export default {
    name: "ImageMessage",
    props: {
        message: {
            type: Object,
            required: true,
        },
        extendedStyle: {
            type: Object,
        },
        messageColors: {
            type: Object,
        },
        received: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            hovered: false,
            previewImg: false,
            showWhatsappChatStarterModal: false,
        };
    },
    computed: {
        isGroupMessage() {
            return !!_.get(this.message, "raw.whatsappAuthor.author");
        },
        whatsappAuthorNumber() {
            return _.get(this.message, "raw.whatsappAuthor.author", "").replace(/[^0-9]/g, "");
        },
        whatsappAuthorName() {
            return _.get(this.message, "raw.whatsappAuthor.authorName", "");
        },
    },
    methods: {
        getId() {
            let id = "";
            if (!!this.message.isMail) {
                id = "img-" + this.message._id;
            } else {
                id = "img-" + this.message.raw.asset._id;
            }
            return id;
        },

        show() {
            const id = this.getId();
            const prefixedId = "img-" + id;
            document.getElementById(prefixedId).click();
        },
        imageSrc() {
            let asset = "";
            if (!!this.message.isMail) {
                asset = this.message;
            } else {
                asset = this.message.raw.asset;
            }
            const path = asset?.path || "";
            if (path === "") return null;
            const isWhatsapp = path.indexOf("whatsapp") !== -1;
            const isInstagram = path.indexOf("instagram") !== -1;
            const isMessenger = path.indexOf("messenger") !== -1;
            const isTelegram = path.indexOf("telegram") !== -1;
            const isFacebook = path.indexOf("facebook") !== -1;
            const isTable = path.indexOf("tables/attachments") !== -1;
            const isUnipile = path.indexOf("unipile") !== -1;
            const isChat = path.indexOf("chat") !== -1;
            const isApiMessages = path.indexOf("api-messages") !== -1;

            const src =
                isWhatsapp || isInstagram || isTelegram || isMessenger || isFacebook || isTable || isUnipile || isChat || isApiMessages
                    ? `https://static.supsis.live/${path}`
                    : asset.v2Format && asset.cdnUrl
                    ? asset.cdnUrl
                    : `https://static.supsis.live/${path + asset._id + (asset.ext ? "." + asset.ext : "")}`;
            return src;
        },
        getCaption() {
            return _.get(
                this.message,
                "caption",
                _.get(
                    this.message,
                    "raw.caption",
                    _.get(
                        this.message,
                        "raw.message",
                        _.get(this.message, "raw.description", _.get(this.message, "data.text", null))
                    )
                )
            );
        },
        getWhatsappNumberColor() {
            if (this.isGroupMessage) {
                const baseColor = "rgb(241, 244, 247)";
                const numberColor = StringUtils.stringToColor(this.whatsappAuthorNumber);
                return this.getContrastColor(baseColor, numberColor);
            }
            return this.messageColors.color;
        },
        getContrastColor(bgColor, textColor) {
            const colors = ["#1a5fb4", "#813d9c", "#c64600", "#2ec27e", "#e01b24", "#3584e4", "#9141ac", "#f5c211"];
            const index = Math.abs(this.hashCode(this.whatsappAuthorNumber)) % colors.length;
            return colors[index];
        },
        hashCode(str) {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = (hash << 5) - hash + char;
                hash = hash & hash; // Convert to 32bit integer
            }
            return hash;
        },
    },
    components: {
        Modal2,
        WhatsappChatStarter,
    },
};
</script>

<style lang="scss" scoped>
.image-message-container {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    padding: 0 12px;

    &.received {
        justify-content: flex-start !important;
    }
}

.message-bubble {
    display: inline-block;
    padding: 8px;
    border-radius: 6px;
    max-width: 300px;
}

.cb-img-preview {
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    img {
        max-width: 100%;
        border-radius: 4px;
    }

    .cb-pr-icon {
        position: absolute;
        z-index: 2;
    }
}

.caption-text {
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.4;
    word-break: break-word;
}

.cb-img-preview:hover {
    cursor: pointer;
    filter: grayscale(100%);
}

.cb-pr-icon {
    position: absolute;
    pointer-events: none;
}

.whatsapp-number {
    cursor: pointer;

    a {
        text-decoration: underline;
    }
}

.whatsapp-number-first {
    cursor: pointer;
    font-size: smaller;
    font-weight: 200;
    letter-spacing: 2px;

    a {
        &:hover {
            text-decoration: underline;
        }
    }

    padding: 5px;
}

.sc-message-meta-new {
    font-size: 8px;
    font-weight: 300;
    color: white;
    text-align: right;
    border-radius: 6px;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 0px !important;
    margin: 5px !important;
    margin-right: 14px !important;
    margin-bottom: 14px !important;
}
</style>
<style lang="scss">
.viewer-open {
    .viewer-container {
        z-index: 9999999 !important;
    }
}
</style>
